import { useCallback, useState, useEffect, createRef } from "react";
import Suggestions from "./Suggestions/Suggestions";
import { useSearch } from "../../Contexts/SearchContext";
import DOMPurify from "dompurify";
import "./SearchBar.scss";
import PreviousSearchTerms from "./PreviousSearchTerms/PreviousSearchTerms"
import PopularSearchTerms from "./PopularSearchTerms/PopularSearchTerms";
import { getSuggestionHandler, postSearchHandler_search } from "../../Api";
import { useOnClickOutsideSelector } from "../../../helpers";
import { getPreviousSearches } from "../../../helpers/site-search";

const SearchBar = () => {
  const { state, dispatch } = useSearch();
  // context
  const {
    searchTerm,
    searchBarHasFocus,
    suggestions,
    selectedFilters,
    popularSearchTerms,
    placeholderText,
  } = state;

  const $searchBar = createRef();
  const searchBarClass = "cmp-site-search__search-bar-section"
  useOnClickOutsideSelector(`.${searchBarClass}`, useCallback(() => { hideSuggestions(); }, []));

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [previousSearchTerms, setPreviousSearchTerms] = useState(getPreviousSearches());

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const hideSuggestions = () => {
    setLocalSearchTerm("");
    dispatch({ type: "UPDATE_SEARCH_BAR_HAS_FOCUS", payload: false });
  }

  const searchBarIsEmpty = () => {
    return !localSearchTerm || localSearchTerm.length === 0;
  }

  // handles
  const handleChange = ({ target }) => {
    const { name, value } = target;
    const sanitizedSearchTerm = DOMPurify.sanitize(value);
    setLocalSearchTerm(sanitizedSearchTerm);
    getSuggestionHandler(sanitizedSearchTerm, dispatch);
  };

  const handleSubmit = (event, suggestion) => {
    const data = new FormData(event?.target);
    let currentSearchTerm = suggestion || data?.get("term");
    event?.preventDefault();
    const url = new URL(window.location.href);
    let currentSearchParams = "";
    if (url.searchParams) {
      currentSearchParams = (" " + url.searchParams.valueOf()).slice(1);
    }

    if (currentSearchTerm && currentSearchTerm !== "null") {
      url.searchParams.set("search", currentSearchTerm);
    } else {
      url.searchParams.delete("search");
    }

    // only push if it changed
    if (url.searchParams && currentSearchParams !== url.searchParams.valueOf()) {
      window.history.pushState({}, "", url.href);
    }

    hideSuggestions();

    // if user is performing a blank search it will reset the page, but if filters are selected we need to search '*'
    if (currentSearchTerm === "" && selectedFilters.length > 0) {
      currentSearchTerm = "*";
    }

    const sanitizedSearchTerm = DOMPurify.sanitize(currentSearchTerm);
    postSearchHandler_search(sanitizedSearchTerm, dispatch);
    dispatch({ type: "UPDATE_SEARCH_TERM", payload: sanitizedSearchTerm });
    updatePreviousSearches(sanitizedSearchTerm);
    $searchBar.current?.querySelector(".site-search-input")?.blur();
    dispatch({ type: "UPDATE_SHOW_RESULTS", payload: true });
  };

  const updatePreviousSearches = (term) => {
    if (term !== "" && !previousSearchTerms.includes(term)) {
      previousSearchTerms.push(term);
      if (previousSearchTerms.length > 5) previousSearchTerms.shift();
      setPreviousSearchTerms(previousSearchTerms);
      sessionStorage.setItem("previousSearches", JSON.stringify(previousSearchTerms));
    }
  }

  const handleSelection = (selection) => {
    setLocalSearchTerm(selection);
    handleSubmit(null, selection);
  };

  return (
    <div
      ref={$searchBar}
      className={`${searchBarClass}${
        searchBarHasFocus ? " search-in-progress" : ""
      }${
        searchBarHasFocus && searchBarIsEmpty() ? " show-start-typing-prompt" : ""
      }`}>
      <form className="form-group" onSubmit={handleSubmit} role={"search"}>
        <div className="cmp-site-search__search-bar-input-container">
          <div className="cmp-site-search__search-bar-input-wrapper">
            <input
              autoComplete="off"
              className="form-control site-search-input"
              name="term"
              value={localSearchTerm}
              placeholder={placeholderText}
              onChange={handleChange}
              onFocus={() => {
                dispatch({ type: "UPDATE_SEARCH_BAR_HAS_FOCUS", payload: true });
              }}
              type="text"
            />
            <span className="start-typing-text">Search...</span>
            <button
                role="button"
                type="button"
                className={"cmp-site-search__search-bar-close"}
                onClick={hideSuggestions}
            >
              <span className="visuallyhidden">Clear Search Term</span>
            </button>
            <button
                role="submit"
                type="submit"
                className={"cmp-site-search__search-bar-submit"}
            >
              <span className="visuallyhidden">Submit Search Term</span>
            </button>
          </div>
        </div>
        <div className={"cmp-site-search__search-bar-dropdown"}>
          <div className={"cmp-site-search__search-bar-dropdown-container"}>
            {searchBarHasFocus && searchBarIsEmpty() && (
              <div className={"cmp-site-search__search-bar-empty-suggestions"}>
                <PreviousSearchTerms
                  handleSelection={handleSelection}
                  previousSearchTerms={previousSearchTerms}
                />
                <PopularSearchTerms
                  handleSelection={handleSelection}
                  popularSearchTerms={popularSearchTerms}
                />
              </div>
            )}
            {searchBarHasFocus && !searchBarIsEmpty() && (
              <Suggestions
                handleSelection={handleSelection}
                suggestions={suggestions}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SearchBar;
